// @mui
import { alpha, styled } from '@mui/material/styles';
import { ListItemText, ListItemIcon, ListSubheader, ListItemButton } from '@mui/material';
// config
import { ICON, NAVBAR } from '../../../config';

// ----------------------------------------------------------------------

export const ListItemStyle = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'isCollapse',
})(({ active, depth, isCollapse, theme }) => ({
  position: 'relative',
  textTransform: 'none',
  fontSize: 14,
  fontWeight: theme.typography.fontWeightMedium,
  paddingLeft: theme.spacing(1.25),
  paddingRight: theme.spacing(1),
  marginBottom: theme.spacing(0.25),
  color: theme.palette.text.secondary,
  borderRadius: theme.spacing(1),
  height: NAVBAR.DASHBOARD_ITEM_ROOT_HEIGHT,
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),
  // Hover (inactive)
  '&:hover': {
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    color: theme.palette.text.primary,
  },
  // Active item (root)
  ...(active && {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.14),
    },
    // Animated left accent bar
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      width: 3,
      height: '55%',
      borderRadius: 8,
      backgroundColor: theme.palette.primary.main,
      transform: 'translateY(-50%)',
      transition: theme.transitions.create(['height', 'opacity'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
  }),
  // Active item (nested) — colour only, no fill/accent (the indent carries hierarchy)
  ...(active &&
    depth !== 1 && {
      color: theme.palette.primary.main,
      backgroundColor: 'transparent',
      '&::before': { display: 'none' },
      '&:hover': {
        backgroundColor: alpha(theme.palette.text.primary, 0.04),
      },
    }),
  // Sub item
  ...(depth && {
    ...(depth > 1 && {
      height: NAVBAR.DASHBOARD_ITEM_SUB_HEIGHT,
      paddingLeft: theme.spacing(depth * 1.25),
    }),
    ...(depth > 2 && {
      paddingLeft: theme.spacing(depth * 1.25),
    }),
  }),
  // The icon rail. The label is gone, so the row is nothing but its icon and
  // the icon belongs in the middle of the rail, not where the text used to
  // start. Last in the block on purpose: it overrides the depth indents above,
  // which mean nothing once there is no text to indent.
  ...(isCollapse && {
    justifyContent: 'center',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    '& .MuiListItemIcon-root': { marginRight: 0, minWidth: 'auto' },
  }),
}));

// ----------------------------------------------------------------------

export const ListItemTextStyle = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'isCollapse',
})(({ isCollapse, theme }) => ({
  transition: theme.transitions.create(['width', 'opacity'], {
    duration: theme.transitions.duration.shorter,
  }),
  ...(isCollapse && {
    width: 0,
    opacity: 0,
  }),
}));

// ----------------------------------------------------------------------

export const ListItemIconStyle = styled(ListItemIcon)(({ theme }) => ({
  width: ICON.NAVBAR_ITEM,
  height: ICON.NAVBAR_ITEM,
  // MUI gives ListItemIcon a 56px minimum, which is why a 20px icon used to sit
  // a thumb's width away from its label and made the whole sidebar read wide.
  // Own the gap instead: the icon box is the icon, and the space after it is a
  // margin we chose.
  minWidth: 28,
  marginRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { width: '100%', height: '100%' },
}));

// ----------------------------------------------------------------------

export const ListSubheaderStyle = styled(ListSubheader)(({ theme }) => ({
  fontSize: 11,
  lineHeight: 1.5,
  fontWeight: theme.typography.fontWeightSemiBold,
  textTransform: 'none',
  letterSpacing: 0.3,
  borderRadius: theme.shape.borderRadius,
  paddingTop: theme.spacing(2),
  paddingLeft: theme.spacing(1.25),
  paddingBottom: theme.spacing(0.5),
  color: theme.palette.text.disabled,
  backgroundColor: theme.palette.background.default,
  transition: theme.transitions.create('opacity', {
    duration: theme.transitions.duration.shorter,
  }),
}));
